import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ShieldCheck, CheckCircle2, XCircle, Clock, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Verificar() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token");
  const [estado, setEstado] = useState("verificando"); // verificando | ok | invalido | caducado
  const [reenviando, setReenviando] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [emailReenvio, setEmailReenvio] = useState("");
  const hecho = useRef(false);

  useEffect(() => {
    if (hecho.current) return;
    hecho.current = true;
    if (!token) { setEstado("invalido"); return; }
    api.post("/registro/verificar", { token })
      .then(() => {
        setEstado("ok");
        setTimeout(() => navigate("/login?verificado=1"), 1800);
      })
      .catch((err) => {
        setEstado(err.response?.status === 410 ? "caducado" : "invalido");
      });
  }, [token, navigate]);

  const reenviar = async () => {
    const email = emailReenvio.trim().toLowerCase();
    if (!email) return toast.error("Escribe tu email");
    setReenviando(true);
    try {
      await api.post("/registro/reenviar", { email });
      toast.success("Si ese email tiene un registro pendiente, te hemos reenviado el enlace.");
      setModalOpen(false);
      setEmailReenvio("");
    } catch { toast.error("No se pudo reenviar ahora mismo"); }
    finally { setReenviando(false); }
  };

  const Icono = { verificando: Loader2, ok: CheckCircle2, invalido: XCircle, caducado: Clock }[estado];
  const color = { verificando: "text-[#0E2A47]", ok: "text-[#10B981]", invalido: "text-[#EF4444]", caducado: "text-[#F59E0B]" }[estado];
  const titulo = { verificando: "Verificando tu cuenta…", ok: "¡Cuenta verificada!", invalido: "Enlace no válido", caducado: "El enlace ha caducado" }[estado];
  const texto = {
    verificando: "Un momento, estamos activando tu cuenta.",
    ok: "Te llevamos a iniciar sesión. Entra con la contraseña que elegiste al registrarte.",
    invalido: "Este enlace de verificación no es válido o ya se usó. Si ya verificaste, inicia sesión.",
    caducado: "Por seguridad, el enlace caduca a las 48 horas. Solicita uno nuevo.",
  }[estado];

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F7F8FA] px-6" data-testid="verificar-page" data-estado={estado}>
      <div className="w-full max-w-md text-center">
        <div className="flex items-center justify-center gap-2 mb-8">
          <ShieldCheck className="text-[#FF5625]" size={28} />
          <span className="font-black text-xl tracking-tighter text-[#0E2A47]">AuditReady<span className="text-[#FF5625]">.</span></span>
        </div>
        <div className="mx-auto h-16 w-16 rounded-full bg-white shadow-sm border border-[#E2E8F0] flex items-center justify-center mb-6">
          <Icono size={30} className={`${color} ${estado === "verificando" ? "animate-spin" : ""}`} />
        </div>
        <h2 className="text-2xl font-black tracking-tight text-[#0E2A47]" data-testid="verificar-titulo">{titulo}</h2>
        <p className="text-[#0E2A47]/70 mt-3">{texto}</p>

        {estado === "caducado" && (
          <Button onClick={() => setModalOpen(true)} data-testid="verificar-reenviar"
            className="w-full mt-6 bg-[#FF5625] hover:bg-[#E0451A] text-white font-semibold">
            Reenviar enlace de verificación
          </Button>
        )}
        {(estado === "ok" || estado === "invalido") && (
          <Link to="/login" data-testid="verificar-ir-login">
            <Button className="w-full mt-6 bg-[#0E2A47] hover:bg-[#163C65] text-white font-semibold">Ir a iniciar sesión</Button>
          </Link>
        )}
      </div>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="bg-white sm:max-w-md" data-testid="reenviar-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#0E2A47]">Reenviar enlace de verificación</DialogTitle>
            <DialogDescription className="text-[#0E2A47]/60 text-sm">
              Escribe el email con el que te registraste y te enviaremos un enlace nuevo (válido 48&nbsp;horas).
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">Email</Label>
            <Input type="email" autoFocus value={emailReenvio}
              onChange={(e) => setEmailReenvio(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") reenviar(); }}
              placeholder="tu@empresa.com" data-testid="reenviar-email" className="mt-1.5" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)} data-testid="reenviar-cancelar">Cancelar</Button>
            <Button onClick={reenviar} disabled={reenviando} data-testid="reenviar-submit"
              className="bg-[#FF5625] hover:bg-[#E0451A] text-white">
              {reenviando ? <><Loader2 size={16} className="mr-2 animate-spin" />Reenviando…</> : "Reenviar enlace"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
